import { useState } from "react";
import { Loader2, Download, ChevronDown } from "lucide-react";
import { KpiCard } from "@/components/kit";
export { downloadPost } from "./ahtShared";

export const fmtAht = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, "0")}`;
};
export const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());
export const fmtDelta = (d, unit = "s") => (d == null ? "—" : `${d > 0 ? "+" : d < 0 ? "-" : ""}${Math.abs(Math.round(d))}${unit}`);

export const fq = (f) => {
  const p = new URLSearchParams();
  Object.entries(f || {}).forEach(([k, v]) => v && p.set(k, v));
  return p.toString();
};

export const thrColor = (val, thr = [360, 420]) => {
  if (val == null) return {};
  if (val <= thr[0]) return { background: "rgb(var(--th-mint) / 0.15)", color: "rgb(var(--th-mint))" };
  if (val <= thr[1]) return { background: "rgb(var(--th-amber) / 0.15)", color: "rgb(var(--th-amber))" };
  return { background: "rgb(var(--th-coral) / 0.15)", color: "rgb(var(--th-coral))" };
};

export const CARD = "mk-card";
export const LBL = "mk-label";
export const SEL = "mk-pill mk-pill-select";

export const Kpi = ({ label, value, sub, accent = "#14B8A6", border, testId }) => (
  <KpiCard testid={testId} label={label} value={value} sub={sub} color={border || accent} big />
);

export const Spark = ({ series, width = 90, height = 26, color }) => {
  const vals = (series || []).filter((v) => v != null);
  if (vals.length < 2) return <span className="text-xs text-thfaint">—</span>;
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const pts = [];
  (series || []).forEach((v, i) => {
    if (v != null) pts.push(`${(i / (series.length - 1)) * width},${height - 3 - ((v - min) / range) * (height - 6)}`);
  });
  const rising = vals[vals.length - 1] > vals[0];
  const c = color || (rising ? "#F43F5E" : "#10B981");
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={pts.join(" ")} fill="none" stroke={c} strokeWidth="1.6" />
    </svg>
  );
};

export const MultiSel = ({ label, options, value, onChange, testId, width = "w-52" }) => {
  const [open, setOpen] = useState(false);
  const sel = value ? value.split("|") : [];
  const toggle = (o) => {
    const next = sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o];
    onChange(next.join("|"));
  };
  return (
    <div className="relative">
      <label className={`${LBL} mb-1.5 block`}>{label}</label>
      <button type="button" data-testid={testId} onClick={() => setOpen(!open)}
        className={`${SEL} ${width} flex items-center justify-between text-left !pr-3`}>
        <span className="truncate">{sel.length ? sel.join(", ") : "All"}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-thdim" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 max-h-64 w-64 overflow-y-auto rounded-lg border border-thborder/12 bg-thcard p-1 shadow-lg">
            <button className="w-full rounded px-2 py-1.5 text-left text-xs text-thcyan hover:bg-thhover/5" onClick={() => { onChange(""); setOpen(false); }}>All</button>
            {options.map((o) => (
              <label key={o} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs text-thbody hover:bg-thhover/5">
                <input type="checkbox" checked={sel.includes(o)} onChange={() => toggle(o)} className="accent-[rgb(var(--th-cyan))]" />
                <span className="truncate">{o}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const Sel = ({ label, options, value, onChange, testId, allLabel = "All", width = "" }) => (
  <div>
    <label className={`${LBL} mb-1.5 block`}>{label}</label>
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={`${SEL} ${width}`}>
      <option value="">{allLabel}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export const FiltersBar = ({ summary, filters, setFilters, showMonths = true }) => {
  const set = (k) => (v) => setFilters({ ...filters, [k]: v });
  return (
    <div data-testid="aht-filter-bar" className="mb-6 flex flex-wrap items-end gap-3">
      <MultiSel label="LOB / Team" options={summary.lobs || []} value={filters.lob} onChange={set("lob")} testId="aht-filter-lob" />
      <Sel label="Category" options={summary.categories || []} value={filters.category} onChange={set("category")} testId="aht-filter-category" />
      <Sel label="Sub Category" options={summary.sub_categories || []} value={filters.sub_category} onChange={set("sub_category")} testId="aht-filter-sub_category" width="max-w-[180px]" />
      <Sel label="Sub-Sub Category" options={summary.sub_subs || []} value={filters.sub_sub} onChange={set("sub_sub")} testId="aht-filter-sub_sub" />
      <Sel label="Language" options={summary.langs || []} value={filters.lang} onChange={set("lang")} testId="aht-filter-lang" />
      <Sel label="Location" options={summary.locations || []} value={filters.location} onChange={set("location")} testId="aht-filter-location" />
      {showMonths && (
        <>
          <Sel label="Month From" options={summary.months || []} value={filters.month_from} onChange={set("month_from")} testId="aht-filter-month_from" allLabel="Any" />
          <Sel label="Month To" options={summary.months || []} value={filters.month_to} onChange={set("month_to")} testId="aht-filter-month_to" allLabel="Any" />
        </>
      )}
      <button data-testid="aht-filter-clear"
        onClick={() => setFilters({ lob: "", category: "", sub_category: "", sub_sub: "", lang: "", location: "", month_from: "", month_to: "" })}
        className="rounded-lg border border-thborder/12 bg-thcard px-3 py-2 text-xs text-thmute hover:text-thcyan">
        ↺ All Months
      </button>
    </div>
  );
};

export const ExpBtn = ({ onClick, busy, children, color = "indigo", testId }) => {
  const styles = {
    indigo: "mk-btn-primary",
    purple: "bg-thblue text-white hover:bg-thblue/85 rounded-full",
    green: "mk-btn-ghost",
  };
  return (
    <button data-testid={testId} onClick={onClick} disabled={busy}
      className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold transition-colors disabled:opacity-40 ${styles[color]}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} {children}
    </button>
  );
};

export const Loading = () => (
  <div className="flex items-center justify-center py-24 text-thcyan"><Loader2 className="h-7 w-7 animate-spin" /></div>
);
